/**
 * Hop & Lift — aerial movement system.
 *
 * Grounded press = hop (upward impulse, momentum cost).
 * Airborne press = toggle lift (sustained directional thrust).
 * Single button, phase-gated to aiming + defensive_movement.
 *
 * Input: ACTION.JUMP virtual key (gamepad A, touch button) + Space (keyboard direct).
 */

import { Keys } from 'bonkjs';
import { HOP, FAST_FALL, MOMENTUM, PLATFORM } from '../constants';
import { mirrorAirborne } from '../physics/transitions';
import type { Player } from '../types';
import type { InputProvider } from '../input';
import { lookupTank } from '../tanks';

export interface HopResult {
  hopped: boolean;
  liftToggled: boolean;
  liftBurst: boolean;
}

/**
 * Update hop/lift state for a player. Called once per fixed update during aiming/defense.
 *
 * @param player - The active player
 * @param input - Input provider (local or remote)
 * @param costFree - true if called from defense air phase (no resource drain)
 * @param actionKey - The ACTION.JUMP key string
 * @param nearGround - true if airborne but feet within NEAR_GROUND_PX of terrain (spatial coyote)
 */
export function updateHopLift(
  player: Player,
  input: InputProvider,
  costFree: boolean,
  actionKey: string,
  nearGround?: boolean,
): HopResult {
  const result: HopResult = { hopped: false, liftToggled: false, liftBurst: false };

  // Tumble gate — no hop during hitstun, but lift allowed during recovery
  const inHitstun = player.tumble && player.hitstunFrames > 0;
  if (inHitstun) return result;

  // Read jump input (ACTION.JUMP virtual key OR Space direct)
  const jumpDown = input.getKey(actionKey) || input.getKey(Keys.Space);

  // Edge-trigger: jumpDown && !player.jumpHeld (same pattern as specialHeld)
  const justPressed = jumpDown && !player.jumpHeld;
  player.jumpHeld = jumpDown;

  // --- Coyote time ---
  // Reset when grounded, increment when airborne
  if (player.grounded) {
    player.coyoteFrames = 0;
  } else {
    if (player.coyoteFrames < HOP.COYOTE_FRAMES + 1) {
      player.coyoteFrames++;
    }
  }

  // --- Jump buffer ---
  if (justPressed && !player.grounded) {
    player.jumpBufferFrames = HOP.BUFFER_FRAMES;
  }
  if (player.jumpBufferFrames > 0) {
    player.jumpBufferFrames--;
  }

  // "Effectively grounded" = grounded OR within coyote time OR near ground (spatial coyote)
  const effectivelyGrounded = player.grounded || player.coyoteFrames <= HOP.COYOTE_FRAMES || !!nearGround;

  // "Should hop" = just pressed while effectively grounded, OR buffered press on landing
  const shouldHop = (justPressed && effectivelyGrounded) ||
    (player.grounded && player.jumpBufferFrames > 0);

  // Get resource pool — costFree mode (defense air phase) has infinite resource and no drain
  const moveCostMult = lookupTank(player.tankId).movementCostMultiplier ?? 1.0;
  const getResource = () => costFree ? Infinity : player.momentum;
  const drainResource = (amount: number) => {
    if (costFree) return;
    const scaled = amount * moveCostMult;
    player.momentum = Math.max(0, player.momentum - scaled);
  };

  // === HOP (grounded impulse) — blocked during tumble recovery ===
  if (shouldHop && !player.tumble && getResource() >= HOP.HOP_MOMENTUM_COST) {
    // Preserves existing vx — walk right + hop = forward arc
    player.vy = HOP.HOP_VY;

    // Deduct cost
    drainResource(HOP.HOP_MOMENTUM_COST);

    // Unsettle
    player.settled = false;
    player.grounded = false;
    player.walkCatchupTimer = 0;
    player.walkDeltaX = 0;
    mirrorAirborne(player);

    // Squash spring
    player.squash = HOP.HOP_SQUASH;
    player.squashVel = HOP.HOP_SQUASH_VEL;

    // Activate momentum if not yet activated
    if (!player.momentumActivated) {
      player.momentumActivated = true;
    }

    // Reset lift state
    player.liftActive = false;

    // Clear buffer + prevent coyote double-hop
    player.jumpBufferFrames = 0;
    player.coyoteFrames = HOP.COYOTE_FRAMES + 1; // overflow → no coyote hop next frame

    result.hopped = true;
    return result;
  }

  // === LIFT TOGGLE (airborne + just pressed + fuel remaining) ===
  if (justPressed && !player.grounded && !effectivelyGrounded && (player.liftActive || player.liftFuel > 0)) {
    const wasOff = !player.liftActive;
    player.liftActive = !player.liftActive;
    result.liftToggled = true;

    // Activation burst — one-time impulse on OFF→ON
    // Directional input = kick in that direction (jetpack). No input = upward kick.
    if (wasOff && player.liftActive && getResource() >= HOP.LIFT_BURST_MOMENTUM_COST) {
      let burstX = 0;
      let burstY = 0;
      if (input.getKey(Keys.ArrowLeft) || input.getKey(Keys.A)) burstX -= 1;
      if (input.getKey(Keys.ArrowRight) || input.getKey(Keys.D)) burstX += 1;
      if (input.getKey(Keys.ArrowUp) || input.getKey(Keys.W)) burstY -= 1;
      if (input.getKey(Keys.ArrowDown) || input.getKey(Keys.S)) burstY += 1;

      if (burstX === 0 && burstY === 0) {
        // No directional input → pure upward kick
        burstY = -1;
      }

      // Normalize + scale — pure direction, no bias
      const mag = Math.sqrt(burstX * burstX + burstY * burstY);
      if (mag > 0) {
        burstX /= mag;
        burstY /= mag;
      }

      player.vx += burstX * HOP.LIFT_BURST_SPEED;
      player.vy += burstY * HOP.LIFT_BURST_SPEED;

      drainResource(HOP.LIFT_BURST_MOMENTUM_COST);
      result.liftBurst = true;
    }
  }

  // === LIFT THRUST (liftActive && airborne) ===
  if (player.liftActive && !player.grounded) {
    // Check resource or fuel — if either empty, force lift off
    if (getResource() <= 0 || player.liftFuel <= 0) {
      player.liftActive = false;
    } else {
      // Drain fuel (1 per frame)
      player.liftFuel--;
      // Drain resource
      drainResource(HOP.LIFT_MOMENTUM_DRAIN);

      // Activate momentum if not yet activated
      if (!player.momentumActivated) {
        player.momentumActivated = true;
      }

      // Compute thrust direction from WASD
      let thrustX = 0;
      let thrustY = 0;
      if (input.getKey(Keys.ArrowLeft) || input.getKey(Keys.A)) thrustX -= 1;
      if (input.getKey(Keys.ArrowRight) || input.getKey(Keys.D)) thrustX += 1;
      if (input.getKey(Keys.ArrowUp) || input.getKey(Keys.W)) thrustY -= 1;
      if (input.getKey(Keys.ArrowDown) || input.getKey(Keys.S)) thrustY += 1;

      if (thrustX === 0 && thrustY === 0) {
        // No input → pure upward thrust (unchanged default)
        thrustY = -1;
      } else if (thrustX !== 0 && thrustY === 0) {
        // Horizontal only → apply upward bias
        thrustY = -Math.sin(HOP.LIFT_UPWARD_BIAS);
        thrustX = thrustX * Math.cos(HOP.LIFT_UPWARD_BIAS);
      }
      // else: explicit vertical (W/S) with or without horizontal → use as-is

      // Normalize
      const mag = Math.sqrt(thrustX * thrustX + thrustY * thrustY);
      if (mag > 0) {
        thrustX /= mag;
        thrustY /= mag;
      }

      // Speed cap — decompose thrust into parallel (along velocity) and perpendicular.
      // Perpendicular always applies (steering, gravity counter). Parallel only under cap.
      const currentSpeed = Math.sqrt(player.vx * player.vx + player.vy * player.vy);
      if (currentSpeed >= HOP.MAX_LIFT_SPEED && currentSpeed > 0) {
        const velNx = player.vx / currentSpeed;
        const velNy = player.vy / currentSpeed;
        const dot = thrustX * velNx + thrustY * velNy;
        if (dot > 0) {
          // Strip the parallel component — keep perpendicular for steering
          thrustX -= dot * velNx;
          thrustY -= dot * velNy;
        }
      }
      player.vx += thrustX * HOP.LIFT_THRUST;
      player.vy += thrustY * HOP.LIFT_THRUST;
    }
  }

  // === Landing reset ===
  if (player.grounded) {
    player.liftActive = false;
    player.liftFuel = HOP.MAX_LIFT_FUEL;
    player.fastFallActive = false;
  }

  return result;
}

export interface FastFallResult {
  activated: boolean;
}

/**
 * Update fast fall state for a player. Called once per fixed update during aiming/defense.
 * Smash-style fast fall: press S/down while airborne and falling to accelerate downward.
 *
 * @param player - The active player
 * @param input - Input provider (local or remote)
 * @param costFree - true if called from defense air phase (skip momentum grant)
 */
export function updateFastFall(
  player: Player,
  input: InputProvider,
  costFree: boolean,
): FastFallResult {
  const result: FastFallResult = { activated: false };

  // Tumble gate — no fast fall during hitstun, allowed during recovery
  const inHitstun = player.tumble && player.hitstunFrames > 0;
  if (inHitstun) {
    player.fastFallActive = false;
    return result;
  }

  // Read down input (S key or ArrowDown)
  const downDown = input.getKey(Keys.S) || input.getKey(Keys.ArrowDown);

  // Edge-trigger: downDown && !player.fastFallHeld
  const justPressed = downDown && !player.fastFallHeld;
  player.fastFallHeld = downDown;

  // Cancel conditions: W/up pressed, lift active, grounded, or hit into hitstun
  const upDown = input.getKey(Keys.W) || input.getKey(Keys.ArrowUp);
  if (player.fastFallActive) {
    if (upDown || player.liftActive || player.grounded) {
      player.fastFallActive = false;
      return result;
    }
  }

  // Drop-through: press down while grounded on wireframe platform → fall through
  // Skip during tumble — ragdoll already passes through wireframe (makePlatformTest)
  if (justPressed && player.grounded && player.onWireframe && !player.tumble) {
    player.platformDropTimer = PLATFORM.DROP_TIMER_FRAMES;
    player.grounded = false;
    player.onWireframe = false;
    player.settled = false;
    player.vy = PLATFORM.DROP_INITIAL_VY;
    player.walkCatchupTimer = 0;
    player.walkDeltaX = 0;
    mirrorAirborne(player);
    return { activated: false };
  }

  // Activation: just pressed S/down, airborne, falling (vy > 0), lift not active
  if (justPressed && !player.grounded && player.vy > 0 && !player.liftActive && !player.fastFallActive) {
    player.fastFallActive = true;
    player.vy += FAST_FALL.KICK_VY;
    player.fastFallTrailCooldown = 0;

    // Momentum grant — risk/reward for committing to fall (skip when cost-free)
    if (!costFree) {
      player.momentum = Math.min(MOMENTUM.ABSOLUTE_MAX, player.momentum + FAST_FALL.MOMENTUM_GRANT);
    }

    result.activated = true;
  }

  return result;
}
